import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyCalderMode({ brand }: ModeRendererProps) {
  // Balanced motion; elegant stability
  // Mobiles, counterweights, suspension
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.paper} />
      
      {/* Central suspension point */}
      <circle cx="397" cy="80" r="8" fill={brand.deep} opacity={0.9} />
      
      {/* Main mobile structure - balanced elements */}
      {/* Level 1 - main arm */}
      <line x1="397" y1="88" x2="397" y2="140" stroke={brand.deep} strokeWidth="3" />
      <line x1="250" y1="140" x2="544" y2="140" stroke={brand.deep} strokeWidth="3" />
      
      {/* Left branch */}
      <line x1="280" y1="140" x2="280" y2="200" stroke={brand.deep} strokeWidth="2" />
      <ellipse cx="280" cy="220" rx="40" ry="30" fill={brand.aqua} opacity={0.7} />
      
      {/* Right branch - subdivides */}
      <line x1="514" y1="140" x2="514" y2="180" stroke={brand.deep} strokeWidth="2" />
      <line x1="450" y1="180" x2="578" y2="180" stroke={brand.deep} strokeWidth="2" />
      
      {/* Right sub-branches */}
      <line x1="465" y1="180" x2="465" y2="240" stroke={brand.deep} strokeWidth="1.5" />
      <circle cx="465" cy="260" r="25" fill={brand.violet} opacity={0.7} />
      
      <line x1="563" y1="180" x2="563" y2="220" stroke={brand.deep} strokeWidth="1.5" />
      <polygon
        points="563,245 548,220 578,220"
        fill={brand.amber}
        opacity={0.7}
      />
      
      {/* Second mobile hanging from left */}
      <line x1="250" y1="140" x2="180" y2="240" stroke={brand.deep} strokeWidth="2" />
      <circle cx="180" cy="260" r="30" fill={brand.amber} opacity={0.7} />
      
      {/* Vasarely optical distortion on mobile elements */}
      {/* Concentric circles on mobile pieces */}
      {Array.from({ length: 6 }).map((_, i) => (
        <circle
          key={`optical-left-${i}`}
          cx="280"
          cy="220"
          r={35 - i * 5}
          fill="none"
          stroke={brand.deep}
          strokeWidth="1"
          opacity={0.3}
        />
      ))}
      
      {Array.from({ length: 5 }).map((_, i) => (
        <circle
          key={`optical-right-${i}`}
          cx="465"
          cy="260"
          r={22 - i * 4}
          fill="none"
          stroke={brand.deep}
          strokeWidth="1"
          opacity={0.3}
        />
      ))}
      
      {/* Complex mobile system - middle section */}
      <g transform="translate(0, 300)">
        <circle cx="150" cy="80" r="6" fill={brand.deep} />
        <line x1="150" y1="86" x2="150" y2="130" stroke={brand.deep} strokeWidth="2" />
        <line x1="100" y1="130" x2="200" y2="130" stroke={brand.deep} strokeWidth="2" />
        
        <line x1="110" y1="130" x2="110" y2="170" stroke={brand.deep} strokeWidth="1.5" />
        <ellipse cx="110" cy="190" rx="30" ry="20" fill={brand.violet} opacity={0.7} />
        
        <line x1="190" y1="130" x2="190" y2="160" stroke={brand.deep} strokeWidth="1.5" />
        <rect x="175" y="165" width="30" height="30" fill={brand.aqua} opacity={0.7} />
      </g>
      
      {/* Another mobile on right */}
      <g transform="translate(450, 320)">
        <circle cx="100" cy="60" r="6" fill={brand.deep} />
        <line x1="100" y1="66" x2="100" y2="110" stroke={brand.deep} strokeWidth="2" />
        <line x1="60" y1="110" x2="140" y2="110" stroke={brand.deep} strokeWidth="2" />
        
        <line x1="70" y1="110" x2="70" y2="150" stroke={brand.deep} strokeWidth="1.5" />
        <circle cx="70" cy="170" r="20" fill={brand.amber} opacity={0.7} />
        
        <line x1="130" y1="110" x2="130" y2="140" stroke={brand.deep} strokeWidth="1.5" />
        <polygon
          points="130,160 115,140 145,140"
          fill={brand.violet}
          opacity={0.7}
        />
      </g>
      
      {/* Counterweight visualization - bottom section */}
      {Array.from({ length: 5 }).map((_, i) => {
        const x = 120 + i * 130;
        const y = 850;
        const weight = [60, 40, 80, 50, 70][i];
        const colors = [brand.aqua, brand.violet, brand.amber, brand.aqua, brand.violet];
        
        return (
          <g key={`weight-${i}`}>
            <line x1={x} y1="800" x2={x} y2={y} stroke={brand.deep} strokeWidth="2" opacity={0.6} />
            <ellipse
              cx={x}
              cy={y + weight / 2}
              rx="35"
              ry={weight / 2}
              fill={colors[i]}
              opacity={0.6}
            />
            <text
              x={x}
              y={y + weight + 30}
              textAnchor="middle"
              fontFamily="monospace"
              fontSize="11"
              fill={brand.deep}
              opacity={0.5}
            >
              {weight}g
            </text>
          </g>
        );
      })}
      
      {/* Balance beam at bottom */}
      <line x1="80" y1="800" x2="714" y2="800" stroke={brand.deep} strokeWidth="1" opacity={0.3} />
      <circle cx="397" cy="800" r="5" fill={brand.deep} opacity={0.5} />
      
      {/* Optical grid showing balance */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 180 - i * 16;
        const offset = i * 8;
        return (
          <rect
            key={`balance-grid-${i}`}
            x={307 + offset}
            y={950 + offset}
            width={size}
            height={size * 0.6}
            fill="none"
            stroke={brand.violet}
            strokeWidth="0.5"
            opacity={0.2}
          />
        );
      })}
    </g>
  );
}
